import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import './LinkPreviewTooltip.scss';
import { config } from '../../config';
import Close from '../icons/Close';

const LinkPreviewTooltip = ({ url, targetElement, onClose, position = 'bottom' }) => {
  const [metadata, setMetadata] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const [isVisible, setIsVisible] = useState(false);
  const tooltipRef = useRef(null);

  useEffect(() => {
    if (!targetElement || !url) return;

    const fetchMetadata = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await fetch(`${config.API_URI}/v1/url-metadata`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({ url })
        });

        if (!response.ok) {
          throw new Error('Failed to fetch metadata');
        }

        const data = await response.json();
        setMetadata(data);
      } catch (err) {
        console.error('Error fetching URL metadata:', err);
        setError('Failed to load preview');
      } finally {
        setLoading(false);
      }
    };

    fetchMetadata();
  }, [url]);

  useEffect(() => {
    if (!targetElement || !tooltipRef.current) return;

    const calculatePosition = () => {
      const rect = targetElement.getBoundingClientRect();
      const tooltipRect = tooltipRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      let top, left;

      switch (position) {
        case 'top':
          top = rect.top - tooltipRect.height - 8;
          left = rect.left + (rect.width / 2) - (tooltipRect.width / 2);
          break;
        case 'bottom':
        default:
          top = rect.bottom + 8;
          left = rect.left + (rect.width / 2) - (tooltipRect.width / 2);
          break;
        case 'left':
          top = rect.top + (rect.height / 2) - (tooltipRect.height / 2);
          left = rect.left - tooltipRect.width - 8;
          break;
        case 'right':
          top = rect.top + (rect.height / 2) - (tooltipRect.height / 2);
          left = rect.right + 8;
          break;
      }

      // Ensure tooltip stays within viewport
      if (left < 8) left = 8;
      if (left + tooltipRect.width > viewportWidth - 8) {
        left = viewportWidth - tooltipRect.width - 8;
      }
      if (top < 8) top = 8;
      if (top + tooltipRect.height > viewportHeight - 8) {
        top = viewportHeight - tooltipRect.height - 8;
      }

      setTooltipPosition({ top, left });
    };

    // Calculate position after a short delay to ensure tooltip is rendered
    const timer = setTimeout(() => {
      calculatePosition();
      setIsVisible(true);
    }, 10);

    return () => clearTimeout(timer);
  }, [targetElement, position, metadata, loading]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (tooltipRef.current && !tooltipRef.current.contains(event.target)) {
        onClose();
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [onClose]);

  const renderVideoEmbed = (video) => {
    if (!video) return null;

    return (
      <div className="link-preview-video">
        <iframe
          src={video.embedUrl}
          title="Video preview"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </div>
    );
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className="link-preview-loading">
          <div className="loading-spinner">
            <span></span>
            <span></span>
            <span></span>
          </div>
        </div>
      );
    }

    if (error) {
      return (
        <div className="link-preview-error">
          <span className="material-symbols-outlined">error</span>
          <span>{error}</span>
        </div>
      );
    }

    if (!metadata) return null;

    return (
      <div className="link-preview-content">
        {metadata.type === 'video' && metadata.video && (
          renderVideoEmbed(metadata.video)
        )}
        
        {metadata.image && metadata.type !== 'video' && (
          <div className="link-preview-image">
            <img src={metadata.image} alt={metadata.title} />
          </div>
        )}
        
        <div className="link-preview-details">
          <div className="link-preview-header">
            <div className="link-preview-title-container">
              {metadata.favicon && (
                <img
                  src={metadata.favicon}
                  alt="Site favicon"
                  className="link-preview-favicon"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              )}
              <h4 className="link-preview-title">{metadata.title}</h4>
            </div>
            <button
              className="link-preview-close"
              onClick={onClose}
              aria-label="Close preview"
            >
              <Close size={20} />
            </button>
          </div>
          
          {metadata.description && (
            <p className="link-preview-description">{metadata.description}</p>
          )}
          
          <div className="link-preview-footer">
            <span className="link-preview-site">{metadata.siteName}</span>
            <a 
              href={metadata.url} 
              target="_blank" 
              rel="noopener noreferrer"
              className="link-preview-url"
            >
              Visit
            </a>
          </div>
        </div>
      </div>
    );
  };

  if (!targetElement) return null;

  return createPortal(
    <div
      ref={tooltipRef}
      className={`link-preview-tooltip ${isVisible ? 'visible' : ''} position-${position}`}
      style={{
        position: 'fixed',
        top: `${tooltipPosition.top}px`,
        left: `${tooltipPosition.left}px`,
        zIndex: 10000
      }}
      onMouseEnter={(e) => {
        // Prevent tooltip from closing when hovering over it
        e.stopPropagation();
      }}
      onMouseLeave={(e) => {
        // Close tooltip when leaving it (unless moving back to the link)
        const relatedTarget = e.relatedTarget;
        const isMovingToLink = relatedTarget && relatedTarget?.closest('a');

        if (!isMovingToLink) {
          setTimeout(() => {
            onClose();
          }, 100);
        }
      }}
    >
      {renderContent()}
    </div>,
    document.body
  );
};

export default LinkPreviewTooltip;
